import { useEffect, useRef, useState } from 'react';

import { HEADER_DESKTOP_PX, HEADER_MOBILE_PX, MOBILE_MQ } from '../../../../theme/tokens';

/**
 * Drives the sticky SectionHead's "pinned" state. A zero-height sentinel sits at
 * the header's top edge; once it scrolls up under the layout top bar the header
 * has pinned, so `stuck` flips true (and the header morphs into a floating pill).
 *
 * Two scroll regimes, one observer: on desktop the page scrolls inside
 * `.ai-bb-page-root` (so that element is the IntersectionObserver root); on mobile
 * the document scrolls, so the viewport (`root: null`) is the reference. The top
 * inset matches PageShell's header height per regime (88 / 78px) — the exact line
 * at which the header pins. Re-wires on the breakpoint flip.
 *
 * `offsetPx` shifts that pin line down — pass it when the sticky element doesn't pin
 * directly under the top bar but beneath another already-pinned element (e.g. a
 * drill-in page's status bar pinning below the sticky BackHeader). It must equal the
 * extra `top` offset the element adds, so `stuck` flips exactly as it pins.
 */
export const useStuckOnPin = (
  enabled: boolean,
  offsetPx = 0,
): { sentinelRef: React.RefObject<HTMLDivElement>; stuck: boolean } => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!enabled || !sentinel || typeof IntersectionObserver === 'undefined') return undefined;

    let observer: IntersectionObserver | null = null;
    const mql = window.matchMedia(MOBILE_MQ);

    const connect = (): void => {
      observer?.disconnect();
      const mobile = mql.matches;
      const root = mobile ? null : sentinel.closest<HTMLElement>('.ai-bb-page-root');
      const headerPx = mobile ? HEADER_MOBILE_PX : HEADER_DESKTOP_PX;
      // Mirror the sticky `top` per regime so `stuck` flips exactly as the header pins.
      // The support-impersonation strip only shifts the mobile pin line: on mobile the
      // header is fixed against the viewport (this observer's root) so the pin line moves
      // down by the strip height; on desktop the strip rides the layout Root's top
      // padding, which already shifts BOTH the sticky scrollport AND this observer's root
      // (`.ai-bb-page-root`) together — so the pin line is unchanged and adding it here
      // would flip `stuck` the strip height too late (matching the old double-counted top).
      const bannerPx = mobile
        ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ows-imp-offset')) || 0
        : 0;
      observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
        root,
        rootMargin: `-${headerPx + offsetPx + bannerPx}px 0px 0px 0px`,
        threshold: 0,
      });
      observer.observe(sentinel);
    };

    connect();
    mql.addEventListener('change', connect);
    return () => {
      observer?.disconnect();
      mql.removeEventListener('change', connect);
    };
  }, [enabled, offsetPx]);

  return { sentinelRef, stuck };
};
